<template>
  <div
    class="modal fade"
    :class="{ show: show }"
    :style="{ display: show ? 'block' : 'none' }"
    tabindex="-1"
    role="dialog"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content rounded-3">
        <!-- Header -->
        <div class="modal-header border-0">
          <h5 class="modal-title fw-bold">Edit User Role</h5>
          <button
            type="button"
            class="btn-close"
            @click="$emit('close')"
          ></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <div class="row g-3">
            <!-- Fullname -->
            <div class="col-md-6">
              <div>
                <label class="form-label"
                  >Fullname<span class="text-danger">*</span></label
                >
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': validationErrors.fullname }"
                  v-model="form.fullname"
                />
                <div v-if="validationErrors.fullname" class="text-danger small">
                  {{ validationErrors.fullname }}
                </div>
              </div>

              <div>
                <label class="form-label"
                  >Email<span class="text-danger">*</span></label
                >
                <input
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': validationErrors.email }"
                  v-model="form.email"
                />
                <div v-if="validationErrors.email" class="text-danger small">
                  {{ validationErrors.email }}
                </div>
              </div>

              <div>
                <label class="form-label"
                  >Phone Number<span class="text-danger">*</span></label
                >
                <div class="input-group">
                  <select
                    class="form-select"
                    v-model="form.countryCode"
                    :class="{ 'is-invalid': validationErrors.phone_number }"
                    style="max-width: 90px"
                  >
                    <option value="+62">+62</option>
                    <option value="+1">+1</option>
                    <option value="+65">+65</option>
                  </select>
                  <input
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.phone_number }"
                    v-model="form.phone_number"
                  />
                </div>
                <div
                  v-if="validationErrors.phone_number"
                  class="text-danger small"
                >
                  {{ validationErrors.phone_number }}
                </div>
              </div>
            </div>

            <!-- Role Selection -->
            <div
              class="col-md-6 d-flex flex-column justify-content-center align-items-start gap-1 p-2 rounded border-0"
              style="
                border-radius: 8px;
                background: var(--Background-Light-Grey, #f4f6f6);
              "
            >
              <label class="form-label"
                >Choose Role<span class="text-danger">*</span></label
              >
              <div class="list-group w-100">
                <label
                  v-for="role in roleList"
                  :key="role.id"
                  class="list-group-item list-group-item-action d-flex gap-3 align-items-start"
                  :class="{ active: form.role_id === role.id }"
                  style="
                    border-radius: 8px;
                    background: var(--Background-White, #fff);
                  "
                >
                  <input
                    class="mt-1"
                    type="radio"
                    :value="role.id"
                    v-model="form.role_id"
                  />
                  <div>
                    <div class="fw-semibold">{{ role.roles_name }}</div>
                    <small class="text-muted">{{ role.description }}</small>
                  </div>
                </label>
              </div>
              <div
                v-if="validationErrors.role_id"
                class="text-danger small mt-1"
              >
                {{ validationErrors.role_id }}
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer border-top justify-content-between">
          <p
            class="fw-normal fst-italic fs-6 lh-base"
            style="color: #3f585a; font-family: Inter; font-size: 12px"
          >
            Notes: “Role changes take effect immediately.”
          </p>
          <div class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="$emit('close')"
              :disabled="isLoading"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-success d-flex align-items-center gap-2"
              @click="saveChanges"
              :disabled="isLoading"
            >
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {{ isLoading ? "Saving..." : "Save Changes" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { updateUser } from "@/api/user-management";

export default {
  name: "EditUserRoleModal",
  props: {
    show: Boolean,
    user: Object,
    roleList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: {
        fullname: this.user?.fullname || "",
        email: this.user?.email || "",
        countryCode: "+62",
        phone_number: this.user?.phone_number || "",
        role_id: this.user?.role_id || "1",
      },
      validationErrors: {},
      isLoading: false,
    };
  },
  methods: {
    validateForm() {
      this.validationErrors = {};

      if (!this.form.fullname || this.form.fullname.trim().length < 3) {
        this.validationErrors.fullname =
          "Fullname must be at least 3 characters.";
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.form.email) {
        this.validationErrors.email = "Email is required.";
      } else if (!emailRegex.test(this.form.email)) {
        this.validationErrors.email = "Please enter a valid email.";
      }

      const phoneRegex = /^[0-9]{8,}$/;
      if (!this.form.phone_number) {
        this.validationErrors.phone_number = "Phone number is required.";
      } else if (!phoneRegex.test(this.form.phone_number)) {
        this.validationErrors.phone_number =
          "Phone number must be at least 8 digits and numeric only.";
      }

      if (!this.form.role_id) {
        this.validationErrors.role_id = "Please select a role.";
      }

      return Object.keys(this.validationErrors).length === 0;
    },
    async saveChanges() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true; // ⬅️ start loading
      try {
        const payload = {
          fullname: this.form.fullname,
          email: this.form.email,
          phone_number: `${this.form.countryCode}${this.form.phone_number}`,
          role_id: this.form.role_id,
        };

        const response = await updateUser(this.user.id, payload);

        if (response && response.status === "success") {
          this.$emit("updated", response.data);
          this.$swal(
            "Success!",
            "User has been updated successfully!",
            "success"
          );
        } else {
          this.$swal(
            "Error",
            response?.message || "Failed to update user",
            "error"
          );
        }
      } catch (error) {
        console.error("Error updating user:", error);
        this.$swal(
          "Error",
          "Something went wrong while updating user",
          "error"
        );
      } finally {
        this.isLoading = false; // ⬅️ stop loading
        this.$emit("close");
      }
    },
  },
  watch: {
    user: {
      handler(newVal) {
        if (newVal) {
          this.form = {
            fullname: newVal.fullname,
            email: newVal.email,
            countryCode: "+62",
            phone_number: newVal.phone_number,
            role_id: newVal.role_id,
          };
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.list-group-item.active {
  color: #000 !important; /* keep text black */
  background-color: #e9ecef !important; /* or your custom background */
  border-color: #dae0e2 !important; /* optional border */
}
</style>
